/* ==========================================================================
   BELORA — SKIMS PURE · product page behaviour
   Measured on live skims.com 2026-09-10 — docs/skims/05-PDP.md
   Reference: references/skims/measurements/g6-gallery.json, g6-pdp-detail.json

   Commerce is Prestige's. <variant-picker> resolves the variant, <product-
   rerender> repaints price/availability/option availability from Shopify, and
   <product-form> adds to cart. Nothing here recalculates commerce; it adds the
   presentation behaviour SKIMS has on top and one honest gate:

   SKIMS ships the PDP with NO size chosen — the button reads "SELECT A SIZE"
   and only becomes "ADD TO BAG" once the customer picks one (measured in
   g1-states.json). Liquid always preselects, so the gate below removes that
   preselection on first load AND disables the form's variant id, so the state
   the customer sees and the state the form would submit never disagree.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-sk-pdp]');
  if (!root) return;

  var DUR = 300;                                     // measured carousel speed
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function buy() { return root.querySelector('[data-sk-buy]'); }
  function form() { return root.querySelector('.sk-buy__form'); }
  function atc() { return root.querySelector('[data-sk-atc]'); }

  /* ================================================== shade-led button === */
  /* Measured on the reference (three colourways of one product, 12 Sep 2026):
     ADD TO BAG — on the page AND on the sticky bar — is filled with the SHADE
     the customer is looking at, and its label flips to onyx on a light shade:
       onyx  rgb(45,45,47)   white label
       sand  rgb(218,192,167) onyx label
       cocoa rgb(73,53,46)   white label
     The paint comes from the chosen dot's own --sk-dot (sk-swatch.liquid), so
     a colourway with no paint — and every product with no colour axis — keeps
     the measured constant #2d2d2f. */
  function shadeButton() {
    // The sticky bar is a SIBLING of [data-sk-pdp], and siblings do not inherit:
    // writing the variables on root left the bar on its default #2d2d2f while
    // the page button wore the shade. The scope has to be the common ancestor,
    // which is the section wrapper.
    var scope = root.closest('[id^="shopify-section"]') || root.parentElement || root;
    // NOT [data-sk-choice]: the colour axis is built WITHOUT that attribute on
    // purpose (the size gate must not wait on a colourway that is already
    // chosen), so scoping to it matched nothing and the shade never reached
    // the button. The dots live on [data-sk-option].
    var dot = root.querySelector('[data-sk-option] .sk-vh:checked + .sk-dot');
    var paint = dot ? (dot.style.getPropertyValue('--sk-dot') || '').trim() : '';
    if (!paint) { scope.style.removeProperty('--sk-atc-bg'); scope.style.removeProperty('--sk-atc-fg'); return; }
    scope.style.setProperty('--sk-atc-bg', paint);
    // Perceived brightness of the paint, so the label stays readable on it.
    var hex = paint.replace('#', '').trim();
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    var n = parseInt(hex, 16);
    if (hex.length !== 6 || isNaN(n)) { scope.style.removeProperty('--sk-atc-fg'); return; }
    var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    var light = (r * 299 + g * 587 + b * 114) / 1000 > 128;
    scope.style.setProperty('--sk-atc-fg', light ? 'var(--sk-onyx)' : 'var(--sk-marble)');
  }

  /* ======================================================== size gate ==== */
  /* One-shot. Opens for good the moment the customer has chosen every axis
     that is theirs to choose, and never interferes again. */
  var gate = {
    open: false,
    chosen: Object.create(null),                     // option position -> true

    required: function () {
      return Array.prototype.map.call(
        root.querySelectorAll('[data-sk-choice]'),
        function (fs) { return fs.getAttribute('data-option-position'); });
    },

    satisfied: function () {
      var req = this.required();
      if (!req.length) return true;                  // nothing for them to pick
      for (var i = 0; i < req.length; i++) if (!this.chosen[req[i]]) return false;
      return true;
    },

    /* Runs on load and after every rerender, until it opens. */
    apply: function () {
      if (this.open) return;

      if (this.satisfied()) { this.release(); return; }

      // Strip the preselection Liquid applied to axes the customer has not
      // chosen. Done without firing change, so Prestige is not asked to
      // resolve a variant that does not exist yet.
      Array.prototype.forEach.call(root.querySelectorAll('[data-sk-choice]'), function (fs) {
        if (gate.chosen[fs.getAttribute('data-option-position')]) return;
        Array.prototype.forEach.call(fs.querySelectorAll('input[data-option-position]'), function (i) { i.checked = false; });
        var v = fs.querySelector('[data-sk-option-value]');
        if (v) {
          if (!v.hasAttribute('data-sk-default')) v.setAttribute('data-sk-default', v.textContent);
          // Only write when it would actually change: an unconditional write
          // emits a mutation record and makes this observer feed itself.
          if (v.textContent !== '') v.textContent = '';
        }
      });

      // Nothing may reach the cart while no real variant is selected.
      // Every write below is conditional. `disabled` is one of the attributes this section's
      // observer listens for, and a same-value attribute write still queues a mutation record —
      // which is exactly how this callback used to feed itself into a renderer crash.
      var f = form();
      if (f && f.id && !f.id.disabled) f.id.disabled = true;

      var b = atc();
      if (b) {
        var label = b.getAttribute('data-label-choose');
        if (label) {
          if (b.textContent.trim() !== label) b.textContent = label;
          if (!b.disabled) b.disabled = true;
          if (b.getAttribute('data-sk-gated') !== 'true') b.setAttribute('data-sk-gated', 'true');
        }
      }
      sticky.sync();
    },

    release: function () {
      this.open = true;
      var f = form();
      if (f && f.id && f.id.disabled) f.id.disabled = false;
      var b = atc();
      if (b && b.hasAttribute('data-sk-gated')) b.removeAttribute('data-sk-gated');
      Array.prototype.forEach.call(root.querySelectorAll('[data-sk-option-value][data-sk-default]'), function (v) {
        if (!v.textContent) v.textContent = v.getAttribute('data-sk-default');
      });
      sticky.sync();
    },
  };

  // A real change on an option input is the customer choosing. Captured on
  // document because <product-rerender> replaces the inputs wholesale.
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('input[data-option-position]')) return;
    if (!root.contains(input)) return;
    gate.chosen[input.getAttribute('data-option-position')] = true;
    if (!gate.open && gate.satisfied()) gate.release();
  }, true);

  /* The gallery's "Kombini Tamamla" chip jumps to the Complete the Look rail. The reference draws its
     chip only on pages that carry that rail (saturation pass N7: 4 of 16 PDPs); a jump to nothing is a
     dead control, so the chip goes when the rail is not on the page. */
  Array.prototype.forEach.call(root.querySelectorAll('.sk-gallery__chip[href="#sk-complete"]'), function (a) {
    var rail = document.getElementById('sk-complete');
    if (!rail || rail.hidden) a.hidden = true;
  });

  /* Model sizing pill: opens and closes its one line. */
  Array.prototype.forEach.call(root.querySelectorAll('[data-sk-model-toggle]'), function (btn) {
    var text = btn.parentElement.querySelector('[data-sk-model-text]');
    if (!text) return;
    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      text.hidden = !open;
    });
  });

  /* ========================================================== gallery ==== */
  var gallery = (function () {
    var wrap = root.querySelector('[data-sk-gallery]');
    var track = wrap && wrap.querySelector('[data-sk-gallery-track]');
    if (!wrap || !track) return { go: function () {}, indexOfMedia: function () { return -1; } };

    var real = Array.prototype.slice.call(track.querySelectorAll('[data-sk-gslide]'));
    var progress = root.querySelector('[data-sk-gprogress]');
    var prev = wrap.querySelector('[data-sk-gallery-prev]');
    var next = wrap.querySelector('[data-sk-gallery-next]');

    /* Gallery video. Measured on the reference's two gallery clips (gs-skvid, 13 Sep 2026): muted,
       looping, inline, no native controls; a clip plays while its slide is the one on screen and is
       paused the moment that slide is left. Its 10 s clip carries a pause control and, having sound, a
       mute control; its silent 4.4 s loop carries neither. Here pause shows for a clip over 5 s (and
       whenever reduced motion means nothing starts by itself), sound only once the clip shows an audio
       track. A customer's own pause holds until they press play; nothing plays while the gallery is
       off screen or the tab is hidden. Every write is conditional: nothing here should churn records. */
    var vids = real.map(function (s) {
      var v = s.querySelector('video');
      if (!v) return null;
      v.muted = true;                  // the property, not only the attribute, is what autoplay policy reads
      v.playsInline = true;
      return { v: v, pause: s.querySelector('[data-sk-gvideo-pause]'), sound: s.querySelector('[data-sk-gvideo-sound]'), held: false };
    });
    var hasVideo = vids.some(Boolean);
    var galleryOnScreen = true;
    function currentReal() {
      return real.length < 2 ? 0 : ((index - 1) + real.length) % real.length;
    }
    function setAttr(el, name, value) {
      if (el.getAttribute(name) !== value) el.setAttribute(name, value);
    }
    function paintVideo(o) {
      if (o.pause) {
        // Long enough to need a control: over 5 s. A file that does not state its duration (Infinity
        // until its end is read — the reference's own WebM loop reports that) is judged by play time
        // instead, so a short silent loop never grows a control.
        var d = o.v.duration;
        if (!o.long) o.long = isFinite(d) ? d > 5 : o.v.currentTime > 5;
        var need = reduced.matches || o.long;
        if (o.pause.hidden === need) o.pause.hidden = !need;
        setAttr(o.pause, 'data-paused', o.v.paused ? 'true' : 'false');
        setAttr(o.pause, 'aria-label', o.pause.getAttribute(o.v.paused ? 'data-label-play' : 'data-label-pause') || '');
      }
      if (o.sound) {
        if (o.sound.hidden && (o.v.mozHasAudio || (o.v.audioTracks && o.v.audioTracks.length > 0) || o.v.webkitAudioDecodedByteCount > 0)) o.sound.hidden = false;
        setAttr(o.sound, 'data-muted', o.v.muted ? 'true' : 'false');
        setAttr(o.sound, 'aria-label', o.sound.getAttribute(o.v.muted ? 'data-label-unmute' : 'data-label-mute') || '');
      }
    }
    function markControls() {
      var o = vids[currentReal()];
      var on = !!(o && ((o.pause && !o.pause.hidden) || (o.sound && !o.sound.hidden)));
      if (wrap.classList.contains('is-video-controls') !== on) wrap.classList.toggle('is-video-controls', on);
    }
    function syncVideos(realIndex) {
      if (!hasVideo) return;
      vids.forEach(function (o, i) {
        if (!o) return;
        // A slide off screen is aria-hidden; its controls must not stay in the Tab order either
        // (gs-vidtest: the sound control of slide 2 was reachable while slide 1 was on screen).
        [o.pause, o.sound].forEach(function (b) {
          if (!b) return;
          if (i === realIndex) { if (b.hasAttribute('tabindex')) b.removeAttribute('tabindex'); }
          else if (b.getAttribute('tabindex') !== '-1') b.setAttribute('tabindex', '-1');
        });
        if (i === realIndex && !o.held && !reduced.matches && !document.hidden && galleryOnScreen) {
          if (o.v.paused) { var p = o.v.play(); if (p && p.catch) p.catch(function () { paintVideo(o); }); }
        } else if (!o.v.paused) {
          o.v.pause();
        }
        paintVideo(o);
      });
      markControls();
    }
    if (hasVideo) {
      vids.forEach(function (o) {
        if (!o) return;
        var repaint = function () { paintVideo(o); markControls(); };
        ['loadedmetadata', 'play', 'pause', 'volumechange', 'timeupdate'].forEach(function (ev) { o.v.addEventListener(ev, repaint); });
        if (o.pause) o.pause.addEventListener('click', function () {
          if (o.v.paused) { o.held = false; var p = o.v.play(); if (p && p.catch) p.catch(function () {}); }
          else { o.held = true; o.v.pause(); }
        });
        if (o.sound) o.sound.addEventListener('click', function () { o.v.muted = !o.v.muted; });
      });
      document.addEventListener('visibilitychange', function () { syncVideos(currentReal()); });
      if (reduced.addEventListener) reduced.addEventListener('change', function () { syncVideos(currentReal()); });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          galleryOnScreen = entries[entries.length - 1].isIntersecting;
          syncVideos(currentReal());
        }).observe(wrap);
      }
    }

    if (real.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
      syncVideos(0);
      return { go: function () {}, indexOfMedia: function () { return -1; } };
    }

    /* Measured: the arrows are never disabled, because the carousel loops.
       A clone at each end lets the wrap-around slide instead of jumping; we
       land on the clone, then silently reseat on the real slide. */
    var head = real[real.length - 1].cloneNode(true);
    var tail = real[0].cloneNode(true);
    [head, tail].forEach(function (c) {
      c.setAttribute('data-sk-clone', 'true');
      c.setAttribute('aria-hidden', 'true');
      c.removeAttribute('data-sk-gslide');
      Array.prototype.forEach.call(c.querySelectorAll('img'), function (i) { i.setAttribute('loading', 'lazy'); });
      // A clone only ever shows for the length of a wrap-around slide: its video never loads or plays,
      // and its controls would be buttons with nothing behind them.
      Array.prototype.forEach.call(c.querySelectorAll('video'), function (v) { v.removeAttribute('autoplay'); v.setAttribute('preload', 'none'); });
      Array.prototype.forEach.call(c.querySelectorAll('.sk-gvideo__btn'), function (b) { b.remove(); });
    });
    track.insertBefore(head, track.firstChild);
    track.appendChild(tail);

    var all = Array.prototype.slice.call(track.children);
    var index = 1;                                   // first REAL slide
    var animating = false;

    function step() {
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return all[0].getBoundingClientRect().width + gap;
    }
    function place(animate) {
      track.classList.toggle('is-dragging', !animate);
      track.style.transform = 'translateX(' + (-index * step()) + 'px)';
      var realIndex = ((index - 1) + real.length) % real.length;
      real.forEach(function (s, i) { s.setAttribute('aria-hidden', i === realIndex ? 'false' : 'true'); });
      syncVideos(realIndex);
      // Progress track: one slide wide, moved by transform, exactly as measured.
      if (progress) {
        progress.style.width = (100 / real.length) + '%';
        progress.style.transform = 'translateX(' + (realIndex * 100) + '%)';
      }
    }
    function settle() {
      // Landed on a clone: reseat on its real twin with no animation.
      if (index === 0) { index = real.length; place(false); }
      else if (index === all.length - 1) { index = 1; place(false); }
      animating = false;
    }
    function go(i, animate) {
      index = i;
      var doAnimate = animate !== false && !reduced.matches;
      place(doAnimate);
      if (!doAnimate) { settle(); return; }
      animating = true;
      window.setTimeout(settle, DUR + 20);
    }

    if (prev) prev.addEventListener('click', function () { if (!animating) go(index - 1); });
    if (next) next.addEventListener('click', function () { if (!animating) go(index + 1); });

    /* Touch: follow the finger, then settle on the nearest slide. */
    var startX = null, startY = null, dx = 0, dragging = false;
    var THRESHOLD = 5;                               // measured Swiper threshold

    wrap.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1 || animating) return;
      startX = e.touches[0].clientX; startY = e.touches[0].clientY; dx = 0; dragging = false;
    }, { passive: true });

    /* Axis lock, the reference's way (its Swiper: touch-action pan-y on the
       carousel, a non-passive touchmove that cancels the page's scroll once the
       gesture is horizontal). The listener was passive and nothing restricted
       the pan, so on an iPhone a sideways swipe with any vertical drift also
       scrolled the page under the finger. Now: until the gesture declares an
       axis nothing is cancelled; if it is vertical it is released to the
       browser for good; once it is horizontal every further move is ours and
       the page stays still. No body lock — vertical scrolling is untouched. */
    wrap.addEventListener('touchmove', function (e) {
      if (startX === null) return;
      var ddx = e.touches[0].clientX - startX, ddy = e.touches[0].clientY - startY;
      if (!dragging) {
        if (Math.abs(ddx) < THRESHOLD && Math.abs(ddy) < THRESHOLD) return;
        if (Math.abs(ddy) >= Math.abs(ddx)) { startX = null; return; }   // vertical: the page's
      }
      if (e.cancelable) e.preventDefault();
      dragging = true;
      dx = ddx;
      track.classList.add('is-dragging');
      track.style.transform = 'translateX(' + (-index * step() + dx) + 'px)';
    }, { passive: false });

    function endDrag() {
      if (startX === null) return;
      var moved = dx;
      startX = null; startY = null; dx = 0;
      if (!dragging) return;
      dragging = false;
      go(Math.abs(moved) > step() * 0.2 ? index + (moved < 0 ? 1 : -1) : index);
    }
    wrap.addEventListener('touchend', endDrag, { passive: true });
    wrap.addEventListener('touchcancel', endDrag, { passive: true });

    /* Keyboard: the carousel is focusable as a group, arrows move it. */
    wrap.setAttribute('tabindex', '0');
    wrap.addEventListener('keydown', function (e) {
      if (animating) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
    });

    window.addEventListener('resize', function () { place(false); }, { passive: true });
    place(false);

    return {
      go: function (realIndex) { if (!animating) go(realIndex + 1); },
      indexOfMedia: function (id) {
        for (var i = 0; i < real.length; i++) {
          if (String(real[i].getAttribute('data-media-id')) === String(id)) return i;
        }
        return -1;
      },
    };
  })();

  /* Follow the variant Prestige selected.

     NOT via the 'variant:change' event: Prestige dispatches it on the form it
     has just replaced, so the event starts on a detached node and never reaches
     document. Verified — the listener fired zero times. The freshly rendered
     <script data-variant> blob is Shopify's own answer and is always present. */
  var lastVariantId = null;
  /* Low stock for the chosen size (reference N10). The section prints the line hidden, carrying the
     ids of the variants that are really low; it shows only once every axis is chosen (gate.open) and
     the chosen variant is one of them. This runs inside the buy-column observer, so the write is
     conditional: a same-value attribute write would still queue a record. */
  function syncLowStock() {
    var n = root.querySelector('[data-sk-lowstock]');
    if (!n) return;
    var el = root.querySelector('variant-picker script[data-variant]');
    var v = null;
    try { v = el ? JSON.parse(el.textContent || '{}') : null; } catch (e) { v = null; }
    var show = !!(gate.open && v && v.id && (n.getAttribute('data-sk-lowstock') || '').indexOf(',' + v.id + ',') !== -1);
    if (n.hidden === show) n.hidden = !show;
  }
  function syncGalleryToVariant() {
    syncLowStock();
    var el = root.querySelector('variant-picker script[data-variant]');
    if (!el) return;
    var v;
    try { v = JSON.parse(el.textContent || '{}'); } catch (e) { return; }
    if (!v || !v.id || v.id === lastVariantId) return;
    lastVariantId = v.id;
    var id = v.featured_media && v.featured_media.id;
    if (!id) return;
    var i = gallery.indexOfMedia(id);
    if (i >= 0) gallery.go(i);
  }

  /* ============================================================= tabs ==== */
  /* Measured as a real tablist, so it behaves like one: click selects, Left /
     Right move, Home / End jump, and only the selected tab is in the tab order
     (roving tabindex) so Tab reaches the panel instead of every tab in turn. */
  /* There is more than one tablist on this page — the info tabs AND the
     BELORA FIT panel's tabs. Binding only the first left the other dead, so
     every tablist is wired, each scoped to its own strip. */
  Array.prototype.forEach.call(root.querySelectorAll('[data-sk-tabs] [role="tablist"]'), function (strip) {
    var list = function () { return Array.prototype.slice.call(strip.querySelectorAll('[data-sk-tab]')); };

    function select(tab, focus) {
      list().forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
      // A tab wider than the strip must be scrolled into view on mobile.
      if (tab.scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    strip.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-sk-tab]');
      if (tab) select(tab, false);
    });

    strip.addEventListener('keydown', function (event) {
      var tabs = list();
      var i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      var next = null;
      if (event.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
      else if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (event.key === 'Home') next = tabs[0];
      else if (event.key === 'End') next = tabs[tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      select(next, true);
    });
  });

  /* ================================================== scroll to reviews == */
  /* The reference drives this glide ITSELF rather than handing it to the
     browser. Proof, in one harness: on skims.com the rating click animates
     (6 change frames, settling ~623ms) while a native
     scrollTo({behavior:'smooth'}) on the SAME page moves in a single frame -
     headless Chromium does not animate the native behaviour. Ours used the
     native one, so its duration was whatever the engine chose and differed
     between Chrome and Safari.

     So the glide is ours now: ~500ms on an ease-in-out, the reference's shape
     (measured 310 -> 3848 with the bulk of the distance in the middle third).
     It yields to the customer - any wheel, touch or key cancels it, the way a
     native smooth scroll does - and reduced motion still jumps. */
  var glide = null;
  function stopGlide() { if (glide) { cancelAnimationFrame(glide.raf); glide = null; } }
  ['wheel', 'touchstart', 'keydown'].forEach(function (t) {
    window.addEventListener(t, stopGlide, { passive: true });
  });
  function glideTo(to, dur, done) {
    stopGlide();
    var from = window.scrollY;
    var dist = to - from;
    if (Math.abs(dist) < 2) { if (done) done(); return; }
    var DUR = dur || 500;
    var t0 = performance.now();
    var own = { raf: 0 };
    glide = own;
    var step = function (now) {
      if (glide !== own) return;           // the customer took over, or a new glide started
      var t = Math.min(1, (now - t0) / DUR);
      // ease-in-out cubic, the curve the reference's frames describe
      var e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      window.scrollTo(0, from + dist * e);
      if (t < 1) own.raf = requestAnimationFrame(step);
      else { glide = null; if (done) done(); }
    };
    own.raf = requestAnimationFrame(step);
  }
  /* Where it lands. Measured 13 Sep 2026 on the reference at 1440, 1024, 768, 390 and 320 (QA4 gs-reviews):
     the reviews heading ends 20px from the top of the viewport at EVERY width — under the reference's own
     sticky header, so the shopper arrives at the review count and summary line, with the heading just above
     them. The earlier landing here (heading clear of the header) was a deliberate departure; the brief now
     asks for the reference's landing. The page can still move under a glide (the phone header collapses
     146 → 94 once scrolled, review media settles), so the landing is re-measured when the glide ends and a
     short glide takes up whatever is left. */
  var LAND = 20;
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-sk-scroll-to]');
    if (!btn || !root.contains(btn)) return;
    var target = document.getElementById(btn.getAttribute('data-sk-scroll-to'));
    if (!target) return;
    var heading = target.querySelector('h2') || target;
    var toOf = function () { return heading.getBoundingClientRect().top + window.scrollY - LAND; };
    if (reduced.matches) { window.scrollTo(0, toOf()); return; }
    glideTo(toOf(), 500, function () {
      var left = heading.getBoundingClientRect().top - LAND;
      if (Math.abs(left) > 2) glideTo(window.scrollY + left, 160);
    });
  });

  /* ======================================================= sticky ATC ==== */
  /* Reverse-engineered from the live reference on 13 Sep 2026, nothing carried over from the earlier
     bar (docs/skims/47-STICKY-ATC.md; QA4 st-discover, st-motion, st-jump, st-foot, st-states):

     - Below 1024 only: at 1440, 1280 and 1024 the reference paints no fixed element at any depth.
     - Two INTERSECTION facts decide it, never a position read:
         above    the page's buy button stopped intersecting the viewport with its top above 0
                  (its bottom edge at 0 is still off, at -2 on; the same edge on the way back up)
         footer   the footer intersects the viewport (off when its top is 2px inside, back on at
                  4px out, both directions — no hysteresis)
       show = above && !footer. Only a CROSSING updates `above`, so a jump that crosses nothing —
       from past the button straight to the top, where the button sits below the fold — leaves
       the bar as it was, exactly as the reference does (st-jump).
     - The decision lands ~170ms after the last change (the reference's class flips 168-184ms after
       the crossing). A reversal shorter than that never reaches the screen: a 150ms back-and-forth
       lets the running slide finish, with no restart (st-motion M3).
     - Not painted before its first reveal; the slide itself is CSS keyframes (sk-pdp.css).
     - One addition the reference never needs: its product pages always leave 1800+ px between the
       button and the footer (reviews, rails, editorial). A short BELORA page can leave almost none —
       47px on ShapeUp, 39 on SmoothForm, below zero on LiftUp (QA4 st-window, 390) — and there the
       rule above would slide the bar in and straight back out under a slow thumb. So the bar is only
       offered when at least half a screen of page lies between the button and the footer. */
  var sticky = (function () {
    var bar = document.querySelector('[data-sk-sticky]');
    var btn = bar && bar.querySelector('[data-sk-sticky-btn]');
    var priceEl = bar && bar.querySelector('[data-sk-sticky-price]');
    var desktop = window.matchMedia('(min-width: 1024px)');
    var LAG = 170;
    var above = false, inFooter = false, shown = false, timer = 0;
    var atcIO = null, observed = null;

    if (!bar) return { sync: function () {}, watch: function () {} };

    // The scroll distance over which the bar may show: button bottom to footer top, less one screen.
    // Both edges move with the page, so this is the page's own length, not a scroll position.
    function room() {
      if (!footer || !observed) return Infinity;
      return footer.getBoundingClientRect().top - observed.getBoundingClientRect().bottom - window.innerHeight;
    }
    function apply() {
      timer = 0;
      var next = above && !inFooter && !desktop.matches && room() >= window.innerHeight / 2;
      if (next === shown) return;
      shown = next;
      bar.classList.remove('is-gone');
      bar.classList.toggle('is-in', shown);
      bar.classList.toggle('is-out', !shown);
      bar.setAttribute('aria-hidden', shown ? 'false' : 'true');
    }
    function later() {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(apply, LAG);
    }
    // Once it has slid away it leaves hit-testing and the accessibility tree.
    bar.addEventListener('animationend', function (event) {
      if (event.target === bar && bar.classList.contains('is-out')) bar.classList.add('is-gone');
    });
    desktop.addEventListener('change', later);

    // The footer is the first footer-group section that takes up room.
    var footer = Array.prototype.filter.call(
      document.querySelectorAll('.shopify-section-group-footer-group'),
      function (s) { return s.offsetHeight > 0; })[0];
    if (footer) {
      new IntersectionObserver(function (entries) {
        inFooter = entries[entries.length - 1].isIntersecting;
        later();
      }, { threshold: 0 }).observe(footer);
    }

    // ONE commerce state: the sticky button clicks the real submit button. It
    // holds no variant of its own, so it cannot add a different one.
    if (btn) {
      btn.addEventListener('click', function () {
        var b = atc();
        if (!b) return;
        if (b.disabled) {
          // Nothing to add yet — send them to the choice instead of failing.
          // Measured on the reference (st-states-sk A, 13 Sep 2026): "Select a
          // Size" scrolls the page's own buy button into view with block 'end',
          // so it lands on the viewport's bottom edge with the sizes just above
          // it, and the bar slides away ~200ms later because that button is
          // visible again. Focus goes to the first size so a keyboard user
          // lands on the choice.
          // Sold out: the reference's "Sold Out" bar opens its waitlist dialog,
          // which carries its own size picker. BELORA's waitlist panel is per
          // size — each sold-out cell opens it for that size — so the honest
          // equivalent is the same trip to the size row.
          b.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'end' });
          var fs = root.querySelector('[data-sk-choice]');
          var first = fs && fs.querySelector('input[data-option-position]');
          if (first) first.focus({ preventScroll: true });
          return;
        }
        b.click();
      });
    }

    function sync() {
      var b = atc();
      if (!btn || !b) return;
      /* While the add is in flight the reference writes "Adding ..." on the page's button AND on
         the bar, in the same frame, with the fill unchanged (st-states-sk A 8: t227 both
         "Adding ...", bg rgb(45,45,47); fr-sk-atc at 1440: 16 frames). Prestige only toggles
         aria-busy, and this runs on that very attribute change with the section's observer
         disconnected, so the text write cannot feed it. Every write is conditional. */
      var adding = b.getAttribute('data-label-adding');
      if (adding) {
        if (b.getAttribute('aria-busy') === 'true') {
          if (b.textContent.trim() !== adding) {
            if (!b.hasAttribute('data-sk-label-was')) b.setAttribute('data-sk-label-was', b.textContent.trim());
            b.textContent = adding;
          }
        } else if (b.hasAttribute('data-sk-label-was')) {
          var was = b.getAttribute('data-sk-label-was');
          b.removeAttribute('data-sk-label-was');
          if (b.textContent.trim() === adding) b.textContent = was;
        }
      }
      btn.disabled = false;                 // stays clickable so it can guide
      btn.textContent = (b.textContent || '').trim();
      btn.classList.toggle('is-gated', b.disabled);
      // While the add is in flight the page's button dims and reads busy; the
      // bar clicks that button, so it wears the same state. Without this a tap
      // on the bar looked like nothing had happened until the bag opened.
      if (b.getAttribute('aria-busy') === 'true') btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
      var p = root.querySelector('[data-sk-price]');
      // Clone, do not copy text. The compare-at and the sale price are two
      // separate spans held apart by a MARGIN, so textContent runs them
      // together into "2,779.00TL2,199.00TL" — the same defect the buy column
      // was fixed for, arriving here through a different door.
      // Clone, do not copy text — and carry only the price the customer pays.
      // The reference's bar shows a single figure; ours has a compare-at too,
      // and 14px of "2,779.00TL 2,199.00TL" does not fit the 124px the bar
      // leaves for it, so it wrapped onto a second line the reference never
      // has. The struck-through price still sits in the buy column above.
      if (priceEl && p) {
        priceEl.textContent = '';
        for (var i = 0; i < p.childNodes.length; i++) {
          var n = p.childNodes[i];
          if (n.nodeType === 1 && n.classList.contains('sk-buy__compare')) continue;
          if (n.nodeType === 1 && n.tagName === 'WBR') continue;
          priceEl.appendChild(n.cloneNode(true));
        }
        priceEl.textContent = (priceEl.textContent || '').trim();
        // The sale red lives on the buy column's wrapper, which is not cloned (sk-pdp.css .is-sale).
        var sale = p.classList.contains('sk-buy__price--sale');
        if (priceEl.classList.contains('is-sale') !== sale) priceEl.classList.toggle('is-sale', sale);
      }
    }

    /* Called on every pass of the buy column's observer, so it only re-points when Prestige has
       actually replaced the button; observing the new one reports its current side at once. */
    function watch() {
      var b = atc();
      if (b === observed) return;
      if (atcIO) atcIO.disconnect();
      observed = b || null;
      if (!b) return;
      atcIO = atcIO || new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        above = !e.isIntersecting && e.boundingClientRect.top < 0;
        later();
      }, { threshold: 0 });
      atcIO.observe(b);
    }

    return { sync: sync, watch: watch };
  })();

  /* ============================================ rerender re-attachment === */
  /* <product-rerender> swaps the whole buy column, so anything holding a
     reference to the old ATC has to be pointed at the new one. */
  var scope = buy();
  if (scope) {
    /* Prestige replaces this subtree ~70ms after a click, only ~20ms after our own gate mutation,
       so the buy column has to be watched to re-apply the gate and re-point the sticky bar.

       THIS OBSERVER USED TO CRASH THE PAGE. The note here previously claimed the callback's writes
       were "idempotent, which removes the feedback loop". They were not: `disabled` is in the
       attributeFilter below, and `b.disabled = true` / `f.id.disabled = true` were written
       unconditionally on every pass. A same-value attribute write still queues a MutationRecord, so
       the callback re-triggered itself forever — measured at 200,000 callbacks and 400,000 records
       within two seconds, which churned the document enough to take sk-media.js's observer with it
       (2,000,000 records) and then killed the renderer outright: "Target crashed", on load, before
       any interaction. Blocking this one file made the page load cleanly (p0-bisect).

       The structural fix is to make the observer deaf to its own work: disconnect, do the work,
       reconnect. Per spec, disconnect() also empties the pending record queue, so nothing our own
       writes queued survives to re-enter.
       The re-arm waits one frame, and that frame is blind: observing again does not replay what
       changed while nothing was listening. This note used to say the re-arm "catches" those changes.
       It did not. Measured 13 Sep 2026 (gs-n10dbg): after a size click Prestige's re-render landed
       inside that frame, the new variant blob was already in place, and the low-stock line stayed
       hidden for the whole 1.8 s read until an unrelated class change woke the observer. So the frame
       now ends with one more pass, run BEFORE observing again: it sees whatever arrived in the gap,
       and its own writes cannot queue records because nothing is listening yet.
       The individual writes are ALSO guarded now (see gate.apply / gate.release) so this cannot come
       back if the wrapper is ever refactored away. Two layers, because one of them was already
       believed sufficient once. */
    var OBS_OPTS = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-busy', 'disabled', 'class'] };
    function pass() {
      gate.apply();
      sticky.watch();
      sticky.sync();
      syncGalleryToVariant();
      shadeButton();
    }
    var mo = new MutationObserver(function () {
      if (mo.__busy) return;
      mo.__busy = true;
      mo.disconnect();
      try {
        pass();
      } finally {
        window.requestAnimationFrame(function () {
          try {
            if (scope.isConnected) pass();
          } finally {
            mo.__busy = false;
            if (scope.isConnected) mo.observe(scope, OBS_OPTS);
          }
        });
      }
    });
    mo.observe(scope, OBS_OPTS);
  }

  /* =================================================== ATC error copy ==== */
  document.addEventListener('cart:error', function (event) {
    var box = root.querySelector('[data-sk-atc-error]');
    var msg = event.detail && (event.detail.message || event.detail.description);
    if (!box || !msg) return;
    box.textContent = msg;
    box.hidden = false;
  });
  document.addEventListener('submit', function (event) {
    if (!root.contains(event.target)) return;
    var box = root.querySelector('[data-sk-atc-error]');
    if (box) box.hidden = true;
  }, true);

  /* ================================================================ go === */
  gate.apply();
  sticky.watch();
  sticky.sync();
  syncGalleryToVariant();
  shadeButton();
  // A shade click does not always re-render the column; listen for it directly.
  root.addEventListener('change', function (e) { if (e.target && e.target.matches && e.target.matches('[data-option-position]')) shadeButton(); });

  /* ------------------------------------------------------------------
     Gallery zoom.

     Prestige's dialog engine does the opening, the focus trap, the scroll
     lock and Escape — the same code path as the bag, so there is one
     behaviour to maintain instead of two. All this adds is: put the slide
     the customer is actually looking at into the stage, at full width, and
     start the view in the middle of the garment.

     The active slide is the one the carousel left at aria-hidden="false";
     reading that rather than the closure's index keeps this independent of
     the carousel's clone bookkeeping.
     ------------------------------------------------------------------ */
  (function () {
    var opener = root.querySelector('.sk-gallery__zoom');
    var wrap = root.querySelector('[data-sk-gallery]');
    var modal = root.querySelector('.sk-zoom');
    if (!opener || !wrap || !modal) return;

    var stage = modal.querySelector('[data-sk-zoom-stage]');
    if (!stage) return;

    /* The reference's zoom has NO entrance: sampled every frame at 1440 and 390, its overlay is
       already at full size and full opacity in the first frame it exists, and it is simply gone in
       the frame after the close — no fade, no scale, and on the phone no sheet slide either
       (pdp-sk-1440.json / pdp-sk-390.json, zoom-open + zoom-close).
       Prestige's x-modal fades 200ms at >=sm and slides a bottom sheet 300ms below it, so this
       instance is handed the dialog base class's own zero-duration controls — the engine's own
       object, so it still carries .finish() for the "immediate" path and a real .finished promise
       for the focus trap. Only this modal is changed; the size guide keeps the theme's behaviour. */
    function stripZoomMotion() {
      if (modal._skNoMotion) return;
      var MC = window.customElements && window.customElements.get('x-modal');
      var base = MC && Object.getPrototypeOf(MC.prototype);
      if (!base || typeof base.createEnterAnimationControls !== 'function') return;
      modal.createEnterAnimationControls = base.createEnterAnimationControls;
      modal.createLeaveAnimationControls = base.createLeaveAnimationControls;
      modal._skNoMotion = true;
    }
    stripZoomMotion();          // again on the first click: x-modal may not be defined yet here

    var target = null;
    function ensureTarget() {
      if (target) return target;
      target = document.createElement('img');
      target.className = 'sk-zoom__img';
      target.setAttribute('data-sk-zoom-img', '');
      target.setAttribute('decoding', 'async');
      target.alt = '';
      // The cursor says zoom-out; make it true.
      target.addEventListener('click', function () {
        if (typeof modal.close === 'function') modal.close();
        else modal.removeAttribute('open');
      });
      stage.appendChild(target);
      return target;
    }

    function activeImage() {
      var slide = wrap.querySelector('[data-sk-gslide][aria-hidden="false"] img');
      // A video slide carries no <img>; fall back to the first real image so
      // the control never opens an empty stage.
      return slide || wrap.querySelector('[data-sk-gslide] img');
    }

    function centre() {
      stage.scrollLeft = Math.max(0, (stage.scrollWidth - stage.clientWidth) / 2);
    }

    // Capture phase: fill the stage before Prestige gets the click and opens
    // the dialog, so the image is already decoding when the panel appears.
    // The control opens; it does not toggle. Testing the [open] attribute alone
    // is not enough: Prestige sets it a frame or two after the click, so a
    // quick second tap lands in that gap and toggles the panel straight back
    // shut. The latch closes the gap and is released when the panel closes.
    var latched = false;
    new MutationObserver(function () {
      if (!modal.hasAttribute('open')) latched = false;
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });

    opener.addEventListener('click', function (e) {
      if (latched || modal.hasAttribute('open')) { e.preventDefault(); e.stopPropagation(); return; }
      latched = true;
      stripZoomMotion();
      var src = activeImage();
      if (!src) return;
      var next = src.getAttribute('src') || src.currentSrc || '';
      if (!next) return;
      var img = ensureTarget();
      if (img.getAttribute('src') !== next) {
        img.setAttribute('src', next);
        img.setAttribute('alt', src.getAttribute('alt') || '');
      }
      // Centre once the panel has a width to measure, and again when the
      // full-resolution file lands and the stage grows.
      requestAnimationFrame(function () { requestAnimationFrame(centre); });
      img.addEventListener('load', centre, { once: true });
    }, true);
  })();

})();

/* ==========================================================================
   BEKLEME LISTESI — tukenen beden hucresi panele acar
   --------------------------------------------------------------------------
   Self-contained on purpose: it observes nothing and mutates no attribute the
   buy column watches, so it cannot re-enter the renderer loop that once froze
   this page (P0, 12 Eyl).

   The sold-out cell stays a <label for=...> so the variant picker's markup is
   untouched; the click is intercepted only when the panel is actually printed,
   which happens only when the owner turned the waitlist on for this product.
   With the panel absent the label behaves exactly as before.

   States: idle -> invalid (our message under the field) -> loading (aria-busy
   on the submit control, so a double submit cannot fire) -> the page posts and
   comes back with either the success or the error paint from Liquid.
   ========================================================================== */
(function () {
  'use strict';
  var panel = document.getElementById('sk-waitlist');
  if (!panel) return;

  var sizeOut = panel.querySelector('[data-sk-wl-size]');
  var sizeIn = panel.querySelector('[data-sk-wl-input]');
  var email = panel.querySelector('#sk-wl-email');
  var form = panel.querySelector('form');
  var invalid = panel.querySelector('[data-sk-wl-invalid]');
  var submit = panel.querySelector('[data-sk-wl-submit]');

  document.addEventListener('click', function (e) {
    var cell = e.target.closest ? e.target.closest('[data-sk-wl-open]') : null;
    if (!cell) return;
    e.preventDefault();                     // the label must not toggle a sold-out input
    var size = cell.getAttribute('data-sk-wl-open') || '';
    if (sizeOut) { sizeOut.textContent = size; sizeOut.hidden = !size; }
    if (sizeIn) sizeIn.value = size;
    if (invalid) invalid.hidden = true;
    panel.setAttribute('open', '');
    window.setTimeout(function () { if (email) email.focus(); }, 120);
  });

  if (form) {
    form.addEventListener('submit', function (e) {
      if (email && !email.checkValidity()) {
        e.preventDefault();
        if (invalid) invalid.hidden = false;
        email.focus();
        return;
      }
      if (invalid) invalid.hidden = true;
      if (submit) { submit.setAttribute('aria-busy', 'true'); submit.disabled = true; }
    });
  }
})();
